import { getEffectiveInvoiceStatus } from './Invoices/invoiceStatus';

export const DASHBOARD_UNAVAILABLE = '-' as const;

export type DashboardStatValue = number | string | typeof DASHBOARD_UNAVAILABLE;

export type DashboardStat = {
  label: string;
  value: DashboardStatValue;
};

export type DashboardCardStats = {
  projects: DashboardStat[];
  tasks: DashboardStat[];
  quotations: DashboardStat[];
  invoices: DashboardStat[];
  certificates: DashboardStat[];
  clients: DashboardStat[];
};

type Row = Record<string, unknown>;
type RowSet = Row[] | null;

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
const LINFINITY_ENTITY = 'linfinity engineering pty ltd';
const EQUILIBRIUM_ENTITY = 'equilibrium engineering pty ltd';
const LINFINITY_INVOICE_DASHBOARD_EMAILS = new Set([
  'yl@linfinityeng.com',
  'jp@linfinityeng.com',
]);
const EQUILIBRIUM_INVOICE_DASHBOARD_EMAILS = new Set([
  'wd@linfinityeng.com',
]);

function stat(label: string, value: DashboardStatValue): DashboardStat {
  return { label, value };
}

function unavailableStats(labels: readonly string[]): DashboardStat[] {
  return labels.map((label) => stat(label, DASHBOARD_UNAVAILABLE));
}

function normalizeText(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeEmail(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function hasText(value: unknown): boolean {
  return normalizeText(value) !== '';
}

function getDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isWithinWindow(value: unknown, now: Date, windowMs: number): boolean {
  const date = getDate(value);
  if (!date) {
    return false;
  }

  const diff = now.getTime() - date.getTime();
  return diff >= 0 && diff <= windowMs;
}

function count(rows: Row[], predicate: (row: Row) => boolean): number {
  return rows.reduce((total, row) => total + (predicate(row) ? 1 : 0), 0);
}

function sum(rows: Row[], predicate: (row: Row) => boolean, getValue: (row: Row) => number): number {
  return rows.reduce((total, row) => total + (predicate(row) ? getValue(row) : 0), 0);
}

function anyField(rows: Row[], fields: readonly string[]): boolean {
  if (rows.length === 0) {
    return true;
  }

  return rows.some((row) => fields.some((field) => hasText(row[field])));
}

function statusValue(row: Row): string {
  return normalizeText(row.Status ?? row.Acceptance ?? row.TaskStatus);
}

function quotationDateValue(row: Row): unknown {
  return row.IssueDate ?? row.Date ?? row.CreatedDate ?? row.ModifiedDate;
}

function priorityValue(row: Row): string {
  return normalizeText(row.Priority);
}

function assignedValue(row: Row): string {
  return normalizeText(row.AssignedToEmail ?? row.AssignedTo);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function amountValue(value: unknown): number {
  const amount = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  return Number.isFinite(amount) ? amount : 0;
}

function invoiceAmount(row: Row): number {
  return amountValue(row.TotalAmountExclGST ?? row.TotalAmount);
}

function invoiceSentDateValue(row: Row): unknown {
  return row.SentDate ?? row.IssueDate ?? row.Created ?? row.CreatedDate ?? row.ModifiedDate;
}

function invoiceDueDateValue(row: Row): unknown {
  return row.DueDate ?? row.IssueDate ?? row.SentDate;
}

function invoicePaidDateValue(row: Row): unknown {
  return row.PaidDate ?? row.ModifiedDate;
}

function clientUpdatedDateValue(row: Row): unknown {
  return row.ModifiedDate ?? row.CreatedDate;
}

function getInvoiceDashboardEntity(userEmail?: string | null): string | null {
  const normalizedEmail = normalizeEmail(userEmail);
  if (LINFINITY_INVOICE_DASHBOARD_EMAILS.has(normalizedEmail)) {
    return LINFINITY_ENTITY;
  }
  if (EQUILIBRIUM_INVOICE_DASHBOARD_EMAILS.has(normalizedEmail)) {
    return EQUILIBRIUM_ENTITY;
  }
  return null;
}

function projectStats(rows: RowSet): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['Active', 'Planning', 'Completed']);
  }

  const activeStatuses = new Set(['active', 'in progress', 'inprogress', 'open']);
  const planningStatuses = new Set(['planning', 'plan', 'draft']);
  const completedStatuses = new Set(['completed', 'complete', 'finished', 'done', 'closed']);

  return [
    stat('Active', count(rows, (row) => activeStatuses.has(statusValue(row)))),
    stat('Planning', count(rows, (row) => planningStatuses.has(statusValue(row)))),
    stat('Completed', count(rows, (row) => completedStatuses.has(statusValue(row)))),
  ];
}

function taskStats(rows: RowSet): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['Active', 'Assigned', 'High Priority']);
  }

  const completedStatuses = new Set(['completed', 'complete', 'done']);
  const highPriorities = new Set(['high', 'critical']);

  return [
    stat('Active', count(rows, (row) => !completedStatuses.has(statusValue(row)))),
    stat(
      'Assigned',
      count(rows, (row) => {
        const assigned = assignedValue(row);
        return assigned !== '' && assigned !== 'unassigned';
      })
    ),
    stat('High Priority', count(rows, (row) => highPriorities.has(priorityValue(row)))),
  ];
}

function quotationStats(rows: RowSet, now: Date): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['60 DAYS', 'Pending', 'Approved']);
  }

  const pendingStatuses = new Set(['pending', 'sent', 'draft']);
  const approvedStatuses = new Set(['approved', 'accepted', 'yes', 'y']);
  const canReadStatus = anyField(rows, ['Status', 'Acceptance']);
  const canReadDate = anyField(rows, ['IssueDate', 'Date', 'CreatedDate', 'ModifiedDate']);
  const recentRows = canReadDate
    ? rows.filter((row) => isWithinWindow(quotationDateValue(row), now, SIXTY_DAYS_MS))
    : [];

  return [
    stat('60 DAYS', canReadDate ? recentRows.length : DASHBOARD_UNAVAILABLE),
    stat(
      'Pending',
      canReadStatus && canReadDate
        ? count(recentRows, (row) => pendingStatuses.has(statusValue(row)))
        : DASHBOARD_UNAVAILABLE
    ),
    stat(
      'Approved',
      canReadStatus && canReadDate
        ? count(recentRows, (row) => approvedStatuses.has(statusValue(row)))
        : DASHBOARD_UNAVAILABLE
    ),
  ];
}

function invoiceStats(rows: RowSet, now: Date, userEmail?: string | null): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['Sent', 'Overdue', 'Paid']);
  }

  const targetEntity = getInvoiceDashboardEntity(userEmail);
  const canReadEntity = anyField(rows, ['Entity']);
  const scopedRows = targetEntity && canReadEntity
    ? rows.filter((row) => normalizeText(row.Entity) === targetEntity)
    : rows;

  if (targetEntity) {
    const canReadAmounts = anyField(scopedRows, ['TotalAmountExclGST', 'TotalAmount']);

    if (!canReadAmounts) {
      return unavailableStats(['Sent', 'Overdue', 'Paid']);
    }

    return [
      stat(
        'Sent',
        formatCurrency(
          sum(
            scopedRows,
            (row) => {
              const status = getEffectiveInvoiceStatus(row);
              return (
                status !== 'Draft' &&
                status !== 'Filed' &&
                status !== 'Superseded' &&
                isWithinWindow(invoiceSentDateValue(row), now, THIRTY_DAYS_MS)
              );
            },
            invoiceAmount
          )
        )
      ),
      stat(
        'Overdue',
        formatCurrency(
          sum(
            scopedRows,
            (row) =>
              getEffectiveInvoiceStatus(row) === 'Overdue' &&
              isWithinWindow(invoiceDueDateValue(row), now, SIXTY_DAYS_MS),
            invoiceAmount
          )
        )
      ),
      stat(
        'Paid',
        formatCurrency(
          sum(
            scopedRows,
            (row) =>
              getEffectiveInvoiceStatus(row) === 'Paid' &&
              isWithinWindow(invoicePaidDateValue(row), now, THIRTY_DAYS_MS),
            invoiceAmount
          )
        )
      ),
    ];
  }

  const canReadStatus = anyField(rows, ['Status', 'PaymentStatus', 'PaidYN', 'DueDate', 'PaidDate']);

  return [
    stat('Sent', canReadStatus ? count(rows, (row) => getEffectiveInvoiceStatus(row) === 'Sent') : DASHBOARD_UNAVAILABLE),
    stat('Overdue', canReadStatus ? count(rows, (row) => getEffectiveInvoiceStatus(row) === 'Overdue') : DASHBOARD_UNAVAILABLE),
    stat('Paid', canReadStatus ? count(rows, (row) => getEffectiveInvoiceStatus(row) === 'Paid') : DASHBOARD_UNAVAILABLE),
  ];
}

function certificateStats(rows: RowSet): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['Issued', 'Pending', 'Expired']);
  }

  const issuedStatuses = new Set(['issued', 'valid', 'active']);
  const pendingStatuses = new Set(['pending']);
  const expiredStatuses = new Set(['expired']);
  const canReadStatus = anyField(rows, ['Status']);

  return [
    stat('Issued', canReadStatus ? count(rows, (row) => issuedStatuses.has(statusValue(row))) : DASHBOARD_UNAVAILABLE),
    stat('Pending', canReadStatus ? count(rows, (row) => pendingStatuses.has(statusValue(row))) : DASHBOARD_UNAVAILABLE),
    stat('Expired', canReadStatus ? count(rows, (row) => expiredStatuses.has(statusValue(row))) : DASHBOARD_UNAVAILABLE),
  ];
}

function clientStats(rows: RowSet, now: Date): DashboardStat[] {
  if (!rows) {
    return unavailableStats(['30 DAYS', 'New', 'Existing']);
  }

  const canReadCreatedDate = anyField(rows, ['CreatedDate']);
  const canReadUpdatedDate = anyField(rows, ['ModifiedDate', 'CreatedDate']);
  const recentUpdatedRows = canReadUpdatedDate
    ? rows.filter((row) => isWithinWindow(clientUpdatedDateValue(row), now, THIRTY_DAYS_MS))
    : [];

  return [
    stat('30 DAYS', canReadUpdatedDate ? recentUpdatedRows.length : DASHBOARD_UNAVAILABLE),
    stat(
      'New',
      canReadCreatedDate && canReadUpdatedDate
        ? count(recentUpdatedRows, (row) => {
            const createdAt = getDate(row.CreatedDate);
            return createdAt !== null && now.getTime() - createdAt.getTime() <= THIRTY_DAYS_MS;
          })
        : DASHBOARD_UNAVAILABLE
    ),
    stat(
      'Existing',
      canReadCreatedDate && canReadUpdatedDate
        ? count(recentUpdatedRows, (row) => {
            const createdAt = getDate(row.CreatedDate);
            return createdAt === null || now.getTime() - createdAt.getTime() > THIRTY_DAYS_MS;
          })
        : DASHBOARD_UNAVAILABLE
    ),
  ];
}

export function createInitialDashboardCardStats(): DashboardCardStats {
  return {
    projects: unavailableStats(['Active', 'Planning', 'Completed']),
    tasks: unavailableStats(['Active', 'Assigned', 'High Priority']),
    quotations: unavailableStats(['60 DAYS', 'Pending', 'Approved']),
    invoices: unavailableStats(['Sent', 'Overdue', 'Paid']),
    certificates: unavailableStats(['Issued', 'Pending', 'Expired']),
    clients: unavailableStats(['30 DAYS', 'New', 'Existing']),
  };
}

export function buildDashboardCardStats(
  datasets: {
    projects: RowSet;
    tasks: RowSet;
    quotations: RowSet;
    invoices: RowSet;
    certificates: RowSet;
    clients: RowSet;
  },
  options: {
    now?: Date;
    userEmail?: string | null;
  } = {}
): DashboardCardStats {
  const now = options.now ?? new Date();

  return {
    projects: projectStats(datasets.projects),
    tasks: taskStats(datasets.tasks),
    quotations: quotationStats(datasets.quotations, now),
    invoices: invoiceStats(datasets.invoices, now, options.userEmail),
    certificates: certificateStats(datasets.certificates),
    clients: clientStats(datasets.clients, now),
  };
}
